import type {
  AmenityFieldDto,
  GetUserPropertyDetailsAmenitiesGroupWebDto,
  UserPropertyMultiEditRequestWebDto,
} from '@zg-rentals/ts-rental-manager-api-client';

export const getDataToSave = ({
  inputs,
  categorizedAmenitiesKeys,
  listingAlias,
  fieldName,
  valueToSave,
}: {
  inputs: Record<string, boolean | string>;
  categorizedAmenitiesKeys: Record<string, Array<string>>;
  listingAlias: string;
  fieldName?: string;
  valueToSave?: string | boolean;
}): UserPropertyMultiEditRequestWebDto => {
  const property = Object.keys(inputs).reduce(
    (a: Record<string, boolean | string>, key) => {
      if (categorizedAmenitiesKeys[key]) {
        categorizedAmenitiesKeys[key].forEach((unique) => (a[unique] = unique === inputs[key]));
      } else {
        a[key] = inputs[key];
      }

      return a;
    },
    { listingAlias },
  );

  if (fieldName) {
    /**
     * if `fieldName` is a `category`, then `valueToSave` is an `apiKeyValue` of such `category`,
     * else `fieldName` is `apiKey` and `valueToSave` is `boolean`
     */
    if (categorizedAmenitiesKeys[fieldName]) {
      categorizedAmenitiesKeys[fieldName].forEach((categoryKey) => (property[categoryKey] = false));
      property[valueToSave as string] = true;
    } else {
      property[fieldName] = valueToSave as boolean;
    }
  }

  return {
    property: {
      ...property,
      listingAlias,
    },
  };
};

/**
 * The listing write that follows a generate call. Amenities the AI returned go on, the rest go off,
 * and a single-choice group the AI said nothing about is left out rather than cleared.
 */
export const buildAiDraftSaveData = ({
  listingAlias,
  groups,
  aiAmenityFields,
  description,
}: {
  listingAlias: string;
  groups: Array<GetUserPropertyDetailsAmenitiesGroupWebDto>;
  aiAmenityFields?: Array<AmenityFieldDto>;
  description?: string;
}): UserPropertyMultiEditRequestWebDto => {
  const aiValueByField = new Map(aiAmenityFields?.map(({ field, value }) => [field, value]));
  const property: Record<string, boolean | string> = { listingAlias };

  // The AI's own fields go on first, so the draft still lands if the listing's amenity groups have
  // not arrived yet. The groups below only refine this: they clear the siblings of a single-choice
  // group and switch off what the AI left out.
  aiValueByField.forEach((value, fieldName) => {
    property[fieldName] = value;
  });

  groups.forEach(({ isUniqueSelection, values }) => {
    if (isUniqueSelection) {
      const aiSelectedValue = values.findLast(({ fieldName }) => aiValueByField.get(fieldName) === true);
      if (!aiSelectedValue) {
        return;
      }

      values.forEach(({ fieldName }) => {
        property[fieldName] = fieldName === aiSelectedValue.fieldName;
      });
      return;
    }

    values.forEach(({ fieldName }) => {
      property[fieldName] = aiValueByField.get(fieldName) === true;
    });
  });

  if (description) {
    property.fullDescription = description;
  }

  return { property: { ...property, listingAlias } };
};

export const getAiFieldNames = (aiAmenityFields?: Array<AmenityFieldDto>): Set<string> =>
  new Set(aiAmenityFields?.map(({ field }) => field));

/** Gates the revert control: the AI has an answer to restore for any field it returned, on or off. */
export const hasAiFieldsInGroups = (
  groups: Array<GetUserPropertyDetailsAmenitiesGroupWebDto>,
  aiAmenityFields?: Array<AmenityFieldDto>,
): boolean => {
  const aiFieldNames = getAiFieldNames(aiAmenityFields);
  return groups.some(({ values }) => values.some(({ fieldName }) => aiFieldNames.has(fieldName)));
};

/**
 * Puts the groups back to exactly what the AI returned. The response lists only the amenities the
 * AI selected, so anything absent from it is turned off.
 *
 * Single-choice groups such as Laundry are the exception: a radio cannot be cleared once set, so a
 * group the AI said nothing about keeps the value it has.
 */
export const restoreAiAmenitySelections = ({
  inputs,
  groups,
  aiAmenityFields,
}: {
  inputs: Record<string, boolean | string>;
  groups: Array<GetUserPropertyDetailsAmenitiesGroupWebDto>;
  aiAmenityFields?: Array<AmenityFieldDto>;
}): Record<string, boolean | string> => {
  const aiValueByField = new Map(aiAmenityFields?.map(({ field, value }) => [field, value]));
  const restoredInputs = { ...inputs };

  groups.forEach(({ category, isUniqueSelection, values }) => {
    if (isUniqueSelection) {
      // Last match wins, matching how the initial render resolves a single-choice group.
      const aiSelectedValue = values.findLast(({ fieldName }) => aiValueByField.get(fieldName) === true);
      if (aiSelectedValue) {
        restoredInputs[category] = aiSelectedValue.fieldName;
      }
      return;
    }

    values.forEach(({ fieldName }) => {
      restoredInputs[fieldName] = aiValueByField.get(fieldName) === true;
    });
  });

  return restoredInputs;
};
